import { useSecurityLoading } from '@/context/SecurityLoadingContext';
import { clearSearchIndexStorage } from '@/lib/search-index-persist';
import { consumePrefetchedToken, clearTokenPrefetch } from '@/lib/token-prefetch';
import { ApiClient } from '@/services/api-client';
import SocketClient from '@/services/socket-client';
import { useAuth, useOrganization, useOrganizationList } from '@clerk/nextjs';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

// Module-scope variable to track the org across ClientProvider remounts.
// When OrganizationGuard unmounts/remounts ClientProvider during an org switch,
// instance refs reset and the ref-based isOrgSwitching detection fails.
// This variable survives remounts (same JS module) but resets on hard page reloads.
let lastInitializedOrgId: string | undefined;
type ClientProviderProps = {
  children: ReactNode;
};
const ClientContext = createContext<{
  socketClient: typeof SocketClient;
  apiClient: typeof ApiClient;
}>({
  socketClient: SocketClient,
  apiClient: ApiClient
});
const ClientProvider = ({
  children
}: ClientProviderProps) => {
  const {
    getToken,
    isLoaded: isAuthLoaded
  } = useAuth();
  const {
    isLoaded: isOrgLoaded,
    organization
  } = useOrganization();
  const {
    isLoaded: isOrgListLoaded,
    setActive,
    userMemberships
  } = useOrganizationList({
    userMemberships: {
      infinite: true
    }
  });
  const [initialized, setInitialized] = useState(false);
  const queryClient = useQueryClient();
  const initialOrgIdRef = useRef<string | undefined>(undefined);
  const initDoneRef = useRef(false);

  // Fast path: when the user already has an active org (99% of cases),
  // skip waiting for useOrganizationList to load — it's only needed
  // for the edge case where no org is active and we need to pick one.
  // This removes one Clerk API call from the critical init waterfall.
  const isLoaded = isAuthLoaded && isOrgLoaded && (organization ? true : isOrgListLoaded);

  // Detect org switch synchronously during render so we can immediately
  // block children from rendering stale data from the previous org.
  // The ref is set during the initialization effect below.
  const isOrgSwitching = !!(initialOrgIdRef.current && organization?.id && initialOrgIdRef.current !== organization.id);

  // When the org changes, do a full page reload to guarantee all state
  // (React tree, TanStack cache, sockets, module-scoped singletons, localStorage)
  // is completely reset. This is simpler and more reliable than trying to
  // surgically clear every piece of cached state.
  useEffect(() => {
    if (!isOrgSwitching) return;

    // Org changed — immediately stop all in-flight activity,
    // then hard-reload to guarantee a completely fresh state.
    // Wrapped in try/finally so the hard reload always fires
    // even if a cleanup step throws.
    try {
      SocketClient.disconnect();
      queryClient.cancelQueries();
      queryClient.clear();
      clearSearchIndexStorage();
      clearTokenPrefetch();
      toast.dismiss();
    } finally {
      window.location.href = '/conversations';
    }
  }, [isOrgSwitching, queryClient]);
  useEffect(() => {
    const initializeClients = async () => {
      if (!isLoaded || initDoneRef.current) return;
      let activeOrgId = organization ? organization.id : undefined;
      if (userMemberships?.data && userMemberships.data.length > 0 && !activeOrgId) {
        activeOrgId = userMemberships.data[0].organization.id;
        setActive?.({
          organization: activeOrgId
        });
      }

      // Detect org switch that the ref-based isOrgSwitching check missed.
      // This happens when OrganizationGuard unmounts/remounts ClientProvider
      // during the switch (Clerk briefly sets org to undefined), resetting
      // all instance refs. The module-scope lastInitializedOrgId survives
      // the remount and lets us detect the change.
      if (activeOrgId && lastInitializedOrgId && lastInitializedOrgId !== activeOrgId) {
        SocketClient.disconnect();
        queryClient.cancelQueries();
        queryClient.clear();
        clearSearchIndexStorage();
        clearTokenPrefetch();
        toast.dismiss();
        lastInitializedOrgId = activeOrgId;
        // Hard reload to guarantee completely fresh state (same as isOrgSwitching path)
        window.location.href = '/conversations';
        return;
      }

      // Validate restored search index belongs to current org.
      // restoreSearchIndexFromStorage runs at module scope (before Clerk loads)
      // and may have restored data from a different org.
      const restoredOrgId = queryClient.getQueryData<string>(['searchIndex.restoredOrgId']);
      if (restoredOrgId && activeOrgId && restoredOrgId !== activeOrgId) {
        // Wrong org's data — clear the search index entries
        for (const c of ['positive', 'negative', 'engaged', 'undetermined']) {
          queryClient.removeQueries({
            queryKey: ['lead.getSearchIndex', c, 'full']
          });
        }
        clearSearchIndexStorage();
      }
      queryClient.removeQueries({
        queryKey: ['searchIndex.restoredOrgId']
      });

      // Use prefetched token if OrganizationGuard already started the fetch,
      // otherwise fall back to a fresh getToken() call.
      const prefetched = consumePrefetchedToken(activeOrgId);
      const accessToken = prefetched ? await prefetched : await getToken({
        template: 'backendJWT',
        organizationId: activeOrgId
      });
      if (accessToken) {
        ApiClient.setAccessToken(accessToken); // Set token before any requests
        SocketClient.setAccessToken(accessToken);
        const tokenGetterFn = async () => {
          const token = await getToken({
            template: 'backendJWT',
            organizationId: activeOrgId
          });
          return token;
        };
        ApiClient.setTokenGetter(tokenGetterFn);
        SocketClient.setTokenGetter(tokenGetterFn);
      }

      // Record the initial org so we can detect switches later
      if (activeOrgId) {
        initialOrgIdRef.current = activeOrgId;
        lastInitializedOrgId = activeOrgId;
      }
      initDoneRef.current = true;
      setInitialized(true);
    };
    initializeClients();
  }, [getToken, isLoaded, setActive, userMemberships?.data, organization, queryClient]);
  const isClientLoading = !initialized || isOrgSwitching;
  const {
    registerLoader
  } = useSecurityLoading();
  useEffect(() => {
    registerLoader('client', isClientLoading);
  }, [isClientLoading, registerLoader]);
  if (isClientLoading) {
    return null;
  }
  return <ClientContext.Provider value={{
    socketClient: SocketClient,
    apiClient: ApiClient
  }}>
      {children}
    </ClientContext.Provider>;
};
const useClients = () => {
  return useContext(ClientContext);
};
export { ClientProvider, useClients };